import { BlogPost, Cause, ContactInfo, SiteContent } from '../types';
import { DATA_CONFIG, debugLog } from '../config/dataConfig';

// Dynamic site content that can be updated without redeployment
let siteContent: SiteContent | null = null;

// Helper function to fetch with timeout
const fetchWithTimeout = async (url: string, timeout: number = DATA_CONFIG.REQUEST_TIMEOUT): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

// Default fallback content
const getDefaultContent = (): SiteContent => ({
  donationButton: {
    text: "Donate Now",
    url: "#",
    enabled: true
  },
  mainCause: {
    title: "Healthcare Access for Lower-Income Families",
    targetAmount: 10000,
    currentAmount: 0,
    description: "Every family deserves access to life-saving healthcare, but hidden costs like transportation, parking, medical equipment, and medications create barriers for many low-income and newcomer families. These challenges often mean delayed treatments, financial hardship, and missed care.",
    missionStatement: "Our foundation is dedicated to breaking down these barriers. We work with hospitals and community partners to provide direct support where it's needed most — from helping families reach appointments, to covering essential treatment costs, to ensuring every child has a fair chance at health and recovery.",
    currentInitiative: {
      title: "Transportation Relief Program with SickKids Hospital",
      description: "No child should miss care because their family can't afford the ride. This pilot program provides Uber ride credits and Presto cards for families traveling frequently to SickKids, distributed confidentially through their Social Work team."
    },
    enabled: true
  },
  contactInfo: {
    email: "info@kuddy.org",
    phone: "+1 (548) 390-4250",
    address: "Brampton, Ontario, Canada",
    socialLinks: {
      facebook: "https://facebook.com/thekuddyfoundation",
      twitter: "https://twitter.com/thekuddyfoundation",
      instagram: "https://instagram.com/kuddyfoundation",
      linkedin: "https://linkedin.com/company/thekuddyfoundation"
    }
  },
  donationForm: {
    enabled: true,
    type: "embedded",
    embedded: {
      url: "https://www.zeffy.com/embed/donation-form/help-kids-get-transportation-access-to-urgent-care",
      title: "Donation form powered by Zeffy",
      height: "450px",
      allowPaymentRequest: true,
      allowTransparency: true
    },
    custom: {
      enabled: false,
      title: "Make a difference: Donate today"
    }
  },
  hero: {
    title: ["One Finger.", "One Moment."],
    subtitle: "Join us Today",
    typingTexts: ["Start change.", "Make impact.", "Begin today."]
  },
  aboutUs: {
    sections: [
      {
        title: "Who We Are",
        content: "The Kuddy Foundation is a Brampton based non-profit that supports families facing financial and healthcare challenges. We work to remove barriers so children and families can live with dignity, access opportunities, and focus on what truly matters: health, growth, and wellbeing."
      },
      {
        title: "Our Vision",
        content: "We believe healthcare and wellness should be fair and accessible for everyone. Our vision is to build stronger, healthier communities starting here in Brampton. As we grow, we aim to expand our reach and create a model of care that uplifts families across Canada and worldwide."
      }
    ],
    images: ["/aboutus_image2.jpg", "/aboutus_image.jpg"]
  },
  team: [
    {
      name: "Gogeethan Gumarathas",
      role: "President",
      quote: "TKF was founded with the vision to create long lasting impact to especially those in need. Seeing the power of those who think they're powerless, gave me the motivation to bring alive this mission."
    }
  ],
  volunteer: {
    title: "Become a\nVolunteer",
    description: "Your time and skills can change lives. Join our network of passionate volunteers and be the force behind positive change.",
    buttonText: "Support the movement today",
    buttonUrl: "/volunteers",
    imageUrl: "/charity_image_helped.png"
  },
  footer: {
    description: "Dedicated to making a positive impact in communities through sustainable programs and compassionate support.",
    copyright: "The Kuddy Foundation. All rights reserved.",
    poweredBy: {
      text: "Powered by NXE",
      url: "https://nexiera.ca"
    },
    quickLinks: [
      { label: "About Us", path: "/about-us" },
      { label: "Our Causes", path: "/causes" },
      { label: "Volunteer", path: "/volunteers" },
      { label: "News & Updates", path: "/blog" }
    ],
    getInvolvedLinks: [
      { label: "Make a Donation", path: "/donation" },
      { label: "Become a Volunteer", path: "/volunteers" },
      { label: "Partner With Us", path: "/contact" }
    ]
  },
  blogPosts: [],
  causes: []
});

export const fetchSiteContent = async (): Promise<SiteContent> => {
  // Return cached content if available
  if (siteContent) {
    debugLog('Returning cached site content');
    return siteContent;
  }
  
  // Try primary data source first (S3 or external URL)
  try {
    debugLog('Attempting to fetch from primary source:', DATA_CONFIG.PRIMARY_DATA_URL);
    const primaryResponse = await fetchWithTimeout(DATA_CONFIG.PRIMARY_DATA_URL);
    
    if (primaryResponse.ok) {
      const data = await primaryResponse.json();
      siteContent = data;
      debugLog('Successfully fetched from primary source');
      return siteContent!;
    } else {
      debugLog('Primary source responded with error:', primaryResponse.status);
      throw new Error(`Primary source returned ${primaryResponse.status}`);
    }
  } catch (primaryError) {
    debugLog('Primary source failed:', primaryError);
    
    // Try fallback data source (local file)
    try {
      debugLog('Attempting to fetch from fallback source:', DATA_CONFIG.FALLBACK_DATA_URL);
      const fallbackResponse = await fetchWithTimeout(DATA_CONFIG.FALLBACK_DATA_URL);
      
      if (fallbackResponse.ok) {
        const data = await fallbackResponse.json();
        siteContent = data;
        debugLog('Successfully fetched from fallback source');
        return siteContent!;
      } else {
        debugLog('Fallback source responded with error:', fallbackResponse.status);
        throw new Error(`Fallback source returned ${fallbackResponse.status}`);
      }
    } catch (fallbackError) {
      debugLog('Fallback source also failed:', fallbackError);
      console.warn('Both primary and fallback data sources failed, using default content');
      
      // Return default content as last resort
      siteContent = getDefaultContent();
      return siteContent;
    }
  }
};

// Export functions to get data from site content
export const getBlogPosts = (siteContent: SiteContent | null): BlogPost[] => {
  return siteContent?.blogPosts || [];
};

export const getCauses = (siteContent: SiteContent | null): Cause[] => {
  return siteContent?.causes || [];
};

export const getContactInfo = (siteContent: SiteContent | null): ContactInfo => {
  return siteContent?.contactInfo || {
    email: 'info@kuddy.org',
    phone: '+1 (548) 390-4250',
    address: 'Brampton, Ontario, Canada',
    socialLinks: {}
  };
};